import React, { useState, useEffect } from 'react';
import io from 'socket.io-client';
import Confetti from 'react-confetti';
import { useWindowSize } from '@react-hook/window-size';
import { CircularProgressbarWithChildren, buildStyles } from 'react-circular-progressbar';
import 'react-circular-progressbar/dist/styles.css';
import useSound from 'use-sound';

import logoPilari from '../assets/images/pilari_dark.png';
import logoGZW from '../assets/images/gzw.png';

const warmupSound = '/sounds/warmup.mp3';
const launchSound = '/sounds/launch.mp3';
const celebrationSound = '/sounds/celebration.mp3';
const cymbalSound = '/sounds/cymbal.mp3';

const COUNTDOWN_SECONDS = 10;

const CountdownCelebrationPage = () => {
  const [seconds, setSeconds] = useState(0);
  const [isCounting, setIsCounting] = useState(false);
  const [showConfetti, setShowConfetti] = useState(false);
  const [soundReady, setSoundReady] = useState(false);
  const [width, height] = useWindowSize();

  const [playWarmup] = useSound(warmupSound, { volume: 1, preload: true });
  const [playLaunch] = useSound(launchSound, { volume: 1, preload: true });
  const [playCelebration, { stop: stopCelebration }] = useSound(celebrationSound, { volume: 1, loop: true, preload: true });
  const [playCymbal] = useSound(cymbalSound, { volume: 1, preload: true });

  useEffect(() => {
    const hasActivated = localStorage.getItem('soundReady') === 'true';
    if (hasActivated) setSoundReady(true);
  }, []);

  const handleActivateSound = () => {
  try {
    const sound = playWarmup();
    if (sound && typeof sound.play === 'function') {
      sound.play().then(() => sound.pause()).catch(() => {});
    }
  } catch (_) {}

  setSoundReady(true);
  localStorage.setItem('soundReady', 'true');
};

  useEffect(() => {
    const socket = io('https://pilari.app');

    socket.on('connect', () => {
      console.log('✅ Socket connected:', socket.id);
    });

    socket.on('launchTriggered', () => {
      console.log('🎯 Launch Triggered');
      if (!soundReady) {
        alert('🔊 Harap klik "Aktifkan Suara" terlebih dahulu agar peluncuran bisa dimulai.');
        return;
      }
      if (!isCounting && seconds === 0 && !showConfetti) {
        setSeconds(COUNTDOWN_SECONDS);
        setIsCounting(true);
        setShowConfetti(false);
        playLaunch();
      }
    });

    socket.on('launchReset', () => {
      console.log('🧹 Launch Reset diterima');
      stopCelebration();
      setSeconds(0);
      setIsCounting(false);
      setShowConfetti(false);
    });

    return () => {
      socket.off('launchTriggered');
      socket.off('launchReset');
      socket.off('connect');
      socket.disconnect();
    };
  }, [soundReady, isCounting, seconds, showConfetti, playLaunch, stopCelebration]);

  useEffect(() => {
    let timer;
    if (isCounting && seconds > 0) {
      if (seconds === 2) playCymbal();
      timer = setTimeout(() => setSeconds((prev) => prev - 1), 1000);
    } else if (isCounting && seconds === 0) {
      setShowConfetti(true);
      setIsCounting(false);
      playCelebration();
    }

    return () => clearTimeout(timer);
  }, [seconds, isCounting, playCelebration, playCymbal]);

  const handleRedirect = () => {
    stopCelebration();
    window.location.href = '/';
  };

  return (
    <div className="min-h-screen flex flex-col items-center justify-center bg-gradient-to-br from-white to-violet-100 px-4 relative overflow-hidden">
      {showConfetti && (
        <Confetti width={width} height={height} numberOfPieces={300} colors={['#7a75b5', '#a69bd6', '#ddd8f3']} />
      )}

      <img src={logoPilari} alt="Logo PILARI" className="w-36 sm:w-44 mt-20 mb-12" />

      <div className="w-full max-w-xl text-center flex flex-col items-center">
        {!showConfetti && (
          <h1 className="text-4xl sm:text-5xl font-extrabold text-[#7a75b5] leading-tight mb-6">
            Peluncuran Resmi <br />
            <span className="text-[#5f5aa6]">Aplikasi PILARI</span>
          </h1>
        )}

        {!soundReady && (
          <button
            onClick={handleActivateSound}
            className="mb-8 px-6 py-2 bg-[#7a75b5] text-white rounded-full hover:bg-[#6a64a1] transition"
          >
            🔊 Aktifkan Suara
          </button>
        )}

        {(isCounting || seconds > 0) && (
          <div className="w-44 h-44 mt-2">
            <CircularProgressbarWithChildren
              value={(COUNTDOWN_SECONDS - seconds) * (100 / COUNTDOWN_SECONDS)}
              maxValue={100}
              styles={buildStyles({
                pathColor: '#7a75b5',
                trailColor: '#eaeaea',
              })}
            >
              <div className="text-5xl font-extrabold text-[#5f5aa6]">{seconds}</div>
            </CircularProgressbarWithChildren>
          </div>
        )}

        {showConfetti && (
          <div className="mt-12 w-full flex flex-col items-center">
            <h2 className="text-3xl sm:text-4xl font-bold text-[#7a75b5] animate-bounce mb-3">
              🎉 PILARI Telah Resmi Diluncurkan!
            </h2>
            <p className="text-[#5f5aa6] text-lg sm:text-xl mb-6 leading-relaxed">
              Terima kasih telah menjadi bagian dari gerakan <br />
              <strong>Pilah Sampah Mandiri</strong>
            </p>
            <button
              onClick={handleRedirect}
              className="mt-2 px-6 py-2 bg-[#7a75b5] text-white rounded-full hover:bg-[#6a64a1] transition"
            >
              Beranda
            </button>
            <p className="text-sm text-[#999] italic mt-8">
              "Satu tindakan kecil untuk lingkungan yang lebih besar"
            </p>
          </div>
        )}
      </div>

      <div className="mb-20 mt-12 flex items-center gap-3 sm:gap-4 justify-center">
        <p className="text-xs text-[#666]">Diselenggarakan oleh</p>
        <img src={logoGZW} alt="Garut Zero Waste" className="w-36 sm:w-44 opacity-90" />
      </div>
    </div>
  );
};

export default CountdownCelebrationPage;
